import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import mapMarkerYellow from "../../assets/images/marker_yellow.png";
import UserSuggestion from "./UserSuggestion";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import { BIBlue, BIGrey, BIRed } from "../../assets/helpers";
import defaultProjectImage from "../../assets/images/placeholder_img.jpg";
import moment from "moment";
import { Carousel } from "react-responsive-carousel";
import {
  BarChart,
  Build,
  BusinessCenter,
  Comment,
  Edit,
  HideImage,
  MoreVert,
  NotificationsOff,
  PauseCircleFilled,
  PlayCircleFilled,
  RemoveRedEye,
  Room
} from "@mui/icons-material";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import postTrackingManager from "../new-community/PostTracking";
import { useDispatch, useSelector } from "react-redux";
import { English } from "../../constant/EnglishFallback";
import {
  setCommunityPostIds,
  setSelectedsite,
  setShowalert,
  setShowReport
} from "../../redux/actions/actions";
import PostComment from "./PostComment";
import "../../assets/css/Homepage.css";
import {
  POSTS_USER_SETTINGS,
  SAVE_FOLLOW_ENTITIES,
  SAVE_POST_PICTURE,
  SAVE_POSTS,
  SAVE_POSTS_USERS,
  SITE_ACTIVE
} from "../../constant/Apipath";
import Pages from "../../constant/ReportModel";
import AddDescription from "../sites/AddDescription";
import AddWebsiteModal from "../common/AddWebsiteModal";
import PictureViewer from "../common/PictureViewer";
import { useNavigate } from "react-router-dom";
import { Postrequestcall } from "../../request/Postrequest";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";

/************* Memoized Video Component (extracted outside Post component) ***************************/

const VideoPlayerComponent = memo(
  ({ postItem }) => {
    const containerRef = useRef(null);
    const videoNode = useRef(null);
    const playerRef = useRef(null);
    const [playerInstance, setPlayerInstance] = useState(null);
    const [playerReady, setPlayerReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isInitializedRef = useRef(false);

    const playerConfig = useMemo(
      () => ({
        controls: true,
        autoplay: false,
        muted: true,
        preload: "metadata",
        fluid: false,
        responsive: false,
        width: "100%",
        height: 500
      }),
      []
    );

    const getVideoSource = useCallback(() => {
      const videoUrl = postItem?.pictureList?.[0]?.pictureUrl;

      if (!videoUrl) {
        console.error("getVideoSource-> No video URL found");
        return null;
      }

      // Check if URL ends with specific extensions
      const lowerUrl = videoUrl.toLowerCase();

      if (lowerUrl.includes(".m3u8")) {
        return { src: videoUrl, type: "application/x-mpegURL" };
      } else if (lowerUrl.includes(".mpd")) {
        return { src: videoUrl, type: "application/dash+xml" };
      } else if (
        lowerUrl.includes(".mp4") ||
        lowerUrl.includes(".webm") ||
        lowerUrl.includes(".mov")
      ) {
        // Default to mp4 type for common video formats
        return { src: videoUrl, type: "video/mp4" };
      } else {
        // If no extension detected, try as mp4
        console.warn("getVideoSource-> Unknown video format, defaulting to mp4:", videoUrl);
        return { src: videoUrl, type: "video/mp4" };
      }
    }, [postItem?.pictureList]);

    const initializePlayer = useCallback(() => {
      if (!videoNode.current || playerRef.current || isInitializedRef.current) return;

      const videoSource = getVideoSource();
      if (!videoSource) {
        setError({ message: "Invalid video source" });
        setIsLoading(false);
        return;
      }

      console.log("initializePlayer-> Initializing with source:", videoSource);
      isInitializedRef.current = true;
      setIsLoading(true);

      try {
        playerRef.current = videojs(
          videoNode.current,
          {
            ...playerConfig,
            sources: [videoSource]
          },
          function onPlayerReady() {
            console.log("Video player is ready");
            setPlayerReady(true);
            setPlayerInstance(playerRef.current);
            setIsLoading(false);
            setError(null);
          }
        );

        // Set up event listeners
        playerRef.current.on("loadedmetadata", () => {
          console.log("Video metadata loaded");
          setIsLoading(false);
        });

        playerRef.current.on("canplay", () => {
          setIsLoading(false);
        });

        playerRef.current.on("play", () => {
          setIsLoading(false);
          // Record view when user plays video
          if (postItem?.id) {
            postTrackingManager.recordView(Number(postItem.id), false);
            console.log(`View recorded for post ${postItem.id} - interaction: video-play`);
          }
        });

        playerRef.current.on("error", (errorEvent) => {
          const player = playerRef.current;
          const mediaError = player?.error();
          console.error("Video player error:", mediaError);
          setError({
            message: mediaError?.message || "Video playback error",
            code: mediaError?.code
          });
          setIsLoading(false);
        });

        // Add keyboard controls
        addKeyboardControls();
      } catch (err) {
        console.error("initializePlayer-> Failed to initialize player:", err);
        setError({ message: err.message || "Failed to initialize video player" });
        setIsLoading(false);
        isInitializedRef.current = false;
      }
    }, [getVideoSource, playerConfig]);

    const addKeyboardControls = () => {
      const handlePlayPause = () => {
        if (!playerRef.current) return;
        playerRef.current.paused() ? playerRef.current.play() : playerRef.current.pause();
      };

      const handleKeyboardEvent = (event) => {
        if (event.key === "Enter" || event.key === "GamepadA") {
          handlePlayPause();
        }
      };

      window.addEventListener("keydown", handleKeyboardEvent);
      playerRef.current.keyboardCleanup = () => {
        window.removeEventListener("keydown", handleKeyboardEvent);
      };
    };

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && playerInstance && playerReady) {
            playerInstance
              .play()
              .catch((err) => console.error("useEffect-> Error As Autoplay Failed: ", err));
          } else if (!entries[0].isIntersecting && playerInstance) {
            playerInstance.pause();
          }
        },
        { threshold: 0.5 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, [playerInstance, playerReady]);

    // Initialize player
    useEffect(() => {
      if (videoNode.current && postItem?.pictureList?.[0]?.pictureUrl) {
        const timer = setTimeout(initializePlayer, 100);
        return () => clearTimeout(timer);
      }
    }, [initializePlayer, postItem?.pictureList]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (playerRef.current) {
          playerRef.current.keyboardCleanup?.();
          playerRef.current.dispose();
          playerRef.current = null;
          isInitializedRef.current = false;
          setPlayerReady(false);
          setPlayerInstance(null);
        }
      };
    }, []);

    // Validate source
    if (!postItem?.pictureList?.[0]?.pictureUrl) {
      console.error("VideoComponent-> No video URL available");
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "500px",
            backgroundColor: "#000",
            borderRadius: "8px",
            color: "white"
          }}>
          <Typography>Video not available</Typography>
        </Box>
      );
    }

    const SimpleLoader = () => (
      <div
        style={{
          position: "absolute",
          top: "43.5%",
          left: "45%",
          zIndex: 10,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          padding: "16px 24px",
          borderRadius: "50%",
          color: "white",
          fontSize: "14px",
          fontWeight: "500"
        }}>
        Loading...
      </div>
    );

    const ErrorMessage = () => (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          zIndex: 10,
          backgroundColor: "rgba(220, 53, 69, 0.9)",
          padding: "20px",
          borderRadius: "8px",
          color: "white",
          textAlign: "center",
          maxWidth: "80%"
        }}>
        <div style={{ fontSize: "24px" }}>⚠️</div>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
            Failed to load video
          </div>
          <div style={{ fontSize: "14px", opacity: 0.9 }}>
            {error?.message || "Please try refreshing the page"}
          </div>
          {error?.code && (
            <div style={{ fontSize: "12px", opacity: 0.7, marginTop: "4px" }}>
              Error Code: {error.code}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <Stack
        ref={containerRef}
        style={{
          position: "relative",
          minHeight: "500px",
          backgroundColor: "#000",
          borderRadius: "8px",
          overflow: "hidden"
        }}>
        {/* Simple loading overlay */}
        {isLoading && <SimpleLoader />}

        {/* Error overlay */}
        {error && <ErrorMessage />}

        {/* Click prevention overlay while loading */}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 5,
              cursor: "not-allowed"
            }}
            onClick={(e) => e.preventDefault()}
          />
        )}

        {/* Video Player */}
        <div style={{ borderRadius: "10px", overflow: "hidden" }}>
          <div
            data-vjs-player
            style={{
              width: "100%",
              height: "500px",
              minHeight: "500px",
              backgroundColor: "#000"
            }}>
            <video
              ref={videoNode}
              className="video-js vjs-default-skin vjs-big-play-centered"
              playsInline
              style={{
                width: "100%",
                height: "500px",
                minHeight: "500px",
                display: "block"
              }}
            />
          </div>
        </div>
      </Stack>
    );
  },
  (prevProps, nextProps) => {
    // Memoization comparison: only re-render if postItem.id or pictureList URL changes
    return (
      prevProps.postItem?.id === nextProps.postItem?.id &&
      prevProps.postItem?.pictureList?.[0]?.pictureUrl ===
        nextProps.postItem?.pictureList?.[0]?.pictureUrl
    );
  }
);

VideoPlayerComponent.displayName = "VideoPlayerComponent";

function Post({ postItem, setPosts, posts, from }) {
  const { multiLanguageData, loginData, communityPostIds } = useSelector((state) => state?.main);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openComment, setOpenComment] = useState(false);
  const [selectPost, setSelectPost] = useState(null);

  const [siteInfo, setSiteInfo] = useState({});
  const [addDescriptionModalOpen, setAddDescriptionModalOpen] = useState(false);
  const [addSiteWebsiteModalOpen, setAddSiteWebsiteModalOpen] = useState(false);

  const [followLoaderId, setFollowLoaderId] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const [pictureViewerOpen, setPictureViewerOpen] = useState(false);
  const [pictureViewIndex, setPictureViewIndex] = useState(0);

  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    TEXT_FOLLOWING = English?.Products?.TEXT_FOLLOWING,
    TEXT_FOLLOW = English?.Products?.TEXT_FOLLOW,
    MORE_MENU_HIDE_POST = English?.Home?.MORE_MENU_HIDE_POST,
    MORE_MENU_REPORT_POST = English?.Home?.MORE_MENU_REPORT_POST,
    MORE_MENU_OFF_NOTIFICATIONS = English?.Home?.MORE_MENU_OFF_NOTIFICATIONS,
    MORE_MENU_ON_NOTIFICATIONS = English?.Home?.MORE_MENU_ON_NOTIFICATIONS,
    ALERT_TEXT_IMPRESSIONS = English?.Home?.ALERT_TEXT_IMPRESSIONS,
    ALERT_IMPRESSIONS_FIRST_PART = English?.Home?.ALERT_IMPRESSIONS_FIRST_PART,
    ALERT_IMPRESSIONS_LAST_PART = English?.Home?.ALERT_IMPRESSIONS_LAST_PART,
    ALERT_VIEWS_SUB_HEADING = English?.Home?.ALERT_VIEWS_SUB_HEADING,
    ALERT_VIEWS_HEADING = English?.Home?.ALERT_VIEWS_HEADING
  } = multiLanguageData?.Products || {};

  const {
    TEXT_ADD_DESCRIPTION = English?.SiteDetail?.TEXT_ADD_DESCRIPTION,
    TEXT_FLAG_ACTIVE = English?.SiteDetail?.TEXT_FLAG_ACTIVE,
    BUTTON_ADD_WEBSITE = English?.SiteDetail?.BUTTON_ADD_WEBSITE,
    TEXT_ADD_PICTURE = English?.SiteDetail?.TEXT_ADD_PICTURE
  } = multiLanguageData?.SiteDetail || {};

  const formattedName = `${postItem?.postedByFirstName?.trim()} ${postItem?.postedByLastName?.trim()}`;

  const postIconStyle = {
    fontSize: "50px",
    color: BIBlue
  };

  const getPostIcon = (postIcon) => {
    switch (postIcon) {
      case "Briefcase":
        return <BusinessCenter sx={postIconStyle} />;
      case "Map Pin":
        return <Room sx={postIconStyle} />;
      case "Wrench":
        return <Build sx={postIconStyle} />;
      case "Pencil":
        return <Edit sx={postIconStyle} />;
      default:
        return;
    }
  };

  const trackPostview = async (postItem) => {
    try {
      const currentTimestamp = moment().format("YYYY-MM-DD HH:mm:ss");
      const payload = {
        UserId: Number(loginData?.userInformation?.UserId),
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            Id: 0,
            PostId: postItem?.id,
            UserId: Number(loginData?.userInformation?.UserId),
            NumberOfImpressions: 1, // Incrementing impressions along with view
            NumberOfViews: 1,
            ImpressionLastDate: "",
            ViewerDate: currentTimestamp,
            ModifiedBy: Number(loginData?.userInformation?.UserId)
          }
        ]
      };
      await Postrequestcall(SAVE_POSTS_USERS, payload, loginData?.accessToken);
    } catch (error) {
      console.error("trackPostview-> Error recording view:", error);
    }
  };

  const renderMessageContent = (postItem) => {
    if (postItem?.message === "<<<picture>>>" || postItem?.message === "<<<video>>>") return "";
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let lines = "";
    const hasTranslation =
      postItem?.translatedMessage &&
      postItem?.translatedMessage?.length !== 0 &&
      postItem?.translatedMessage !== "<<<picture>>>" &&
      postItem?.translatedMessage !== "<<<video>>>";
    if (hasTranslation) {
      lines = postItem?.translatedMessage?.split("\n");
    } else {
      lines = postItem?.message?.split("\n");
    }
    return lines?.map((line, index) => {
      const parts = line.split(urlRegex);
      return (
        <span key={index}>
          {parts.map((part, i) =>
            urlRegex.test(part) ? (
              <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackPostview(postItem)}>
                {part}
              </a>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
          {index < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  const handleCommentToggler = (post) => {
    // Implement comment toggling logic here
    setOpenComment((prev) => !prev);
    setSelectPost(post);
    recordPostView("comment");
  };

  const handleShowAlert = (type) => {
    let alertObj = {};
    if (type === "postViewsModal") {
      alertObj = {
        showAlert: true,
        title: ALERT_VIEWS_HEADING,
        text: (
          <>
            <span style={{ color: BIBlue }}>
              <b>{postItem?.nrOfUsersViewedPost}</b>
            </span>{" "}
            {ALERT_VIEWS_SUB_HEADING}
          </>
        )
      };
    } else if (type === "postImpressionModal") {
      alertObj = {
        showAlert: true,
        title: ALERT_TEXT_IMPRESSIONS,
        text: (
          <>
            {ALERT_IMPRESSIONS_FIRST_PART}{" "}
            <span style={{ color: BIBlue }}>
              <b>{postItem?.nrOfUsersImpressionsPerPost}</b>
            </span>{" "}
            {ALERT_IMPRESSIONS_LAST_PART}
          </>
        )
      };
    }
    dispatch(setShowalert(alertObj));
  };

  // Hide Post Function
  const handleHidePost = async (item) => {
    try {
      const hidePostObj = {
        UserId: Number(loginData?.userInformation?.UserId),
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            PostId: Number(item?.id),
            UserId: Number(loginData?.userInformation?.UserId),
            IsHidden: true,
            IsMute: true
          }
        ]
      };
      let hidePostRes = await Postrequestcall(
        POSTS_USER_SETTINGS,
        hidePostObj,
        loginData?.accessToken
      );
      if (hidePostRes?.status === 200) {
        let getPosts = posts.filter((post) => post.id !== item?.id);
        setPosts(getPosts); // Update the state to remove the post from UI
        let updateCommunitypostIds = communityPostIds?.all?.filter((post) => post?.id !== item?.id);
        switch (item?.appName) {
          case "Product":
            let getProductIds = communityPostIds?.products?.filter((post) => post?.id !== item?.id);
            dispatch(
              setCommunityPostIds({
                ...communityPostIds,
                all: updateCommunitypostIds,
                products: getProductIds
              })
            );
            break;
          case "UserSiteProject":
            let getProjectIds = communityPostIds?.projects?.filter((post) => post?.id !== item?.id);
            dispatch(
              setCommunityPostIds({
                ...communityPostIds,
                all: updateCommunitypostIds,
                projects: getProjectIds
              })
            );
            break;
          case "Trade":
            let getTrades = communityPostIds?.trades?.filter((post) => post?.id !== item?.id);
            dispatch(
              setCommunityPostIds({
                ...communityPostIds,
                all: updateCommunitypostIds,
                trades: getTrades
              })
            );
            break;
          default:
            dispatch(
              setCommunityPostIds({
                ...communityPostIds,
                all: updateCommunitypostIds
              })
            );
            break;
        }
        handleMenuClose(); // Close the menu after hiding the post
      }
    } catch (error) {
      console.error("handleHidePost-> Error: ", error);
      handleMenuClose();
    }
  };

  // Report Post Function
  const handleReportPost = async (postItem) => {
    try {
      handleMenuClose();
      const reportObj = {
        showReport: true,
        name: Pages?.post?.name,
        AppScreenId: Pages?.post?.id,
        recordId: postItem?.id || 0,
        appName: postItem?.appName || ""
      };
      dispatch(setShowReport(reportObj));
    } catch (error) {
      console.error("handleReportPost-> Error: ", error);
      handleMenuClose();
    }
  };

  // Mute/Unmute Post Function
  const handleMutePost = async (id, currentMuteStatus) => {
    try {
      const mutePostObj = {
        UserId: Number(loginData?.userInformation?.UserId),
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            PostId: Number(id),
            UserId: Number(loginData?.userInformation?.UserId),
            IsMute: !currentMuteStatus
          }
        ]
      };
      let mutePostRes = await Postrequestcall(
        POSTS_USER_SETTINGS,
        mutePostObj,
        loginData?.accessToken
      );
      if (mutePostRes?.status === 200) {
        // Update the post mute status in state
        const updatedPosts = posts.map((post) => {
          if (post.id === id) {
            return { ...post, isMute: !currentMuteStatus };
          }
          return post;
        });
        setPosts(updatedPosts);
      }
      handleMenuClose();
    } catch (error) {
      console.error("handleMutePost-> Error: ", error);
      handleMenuClose();
    }
  };

  // Handle site website save
  const onSiteWebsiteSave = (type) => {
    // Refresh or update logic here if needed
    setAddSiteWebsiteModalOpen(false);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (postItem) => {
    // Implement navigation logic here, e.g., using React Router

    if (from === "productDetail" || from === "projectDetailPage" || from === "tradeDetail") {
      if (postItem?.postedByUserId !== Number(loginData?.userInformation?.UserId)) {
        navigate(`/${postItem?.postedByUserId}`);
      } else {
        navigate(`/${loginData?.userInformation?.UserId}`);
      }
      return;
    }

    switch (postItem?.appId) {
      case 24:
        // Navigate to project details page
        navigate(`/project/${postItem?.appRecordId}`);
        break;
      case 25:
        // Navigate to profile page
        if (postItem?.postedByUserId !== Number(loginData?.userInformation?.UserId)) {
          navigate(`/${postItem?.postedByUserId}`);
        } else {
          navigate(`/${loginData?.userInformation?.UserId}`);
        }
        break;
      case 4:
        // Navigate to Product details page
        navigate(`/product/${postItem?.appRecordId}`);
        break;
      case 7:
        // Navigate to Trade details page
        navigate(`/trade/${postItem?.appRecordId}`);
        break;
    }
  };

  // Helper function to record post views
  const recordPostView = (interactionType = "general") => {
    if (postItem?.id) {
      postTrackingManager.recordView(Number(postItem?.id), false); // false = count all views, not unique per session
    }
  };

  const NoImageFound = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "12px",
          p: 4,
          bgcolor: "#f9f9f9",
          border: "2px dashed #e0e0e0",
          minHeight: "200px"
        }}>
        <HideImage
          sx={{
            fontSize: 64,
            color: BIGrey,
            opacity: 0.6,
            mb: 2
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: BIGrey,
            textAlign: "center",
            fontWeight: 500,
            opacity: 0.8
          }}>
          Picture has been removed
        </Typography>
      </Box>
    );
  };

  const StatsItem = ({ icon, label, count, disabled, callFun }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: { xs: 0.5, sm: 1 },
        py: 0.5,
        borderRadius: "8px",
        transition: "all 0.2s ease",
        cursor: disabled ? "default" : "pointer",
        "&:hover": {
          backgroundColor: disabled ? "transparent" : `${BIBlue}08`,
          transform: disabled ? "none" : "translateY(-2px)"
        }
      }}
      onClick={callFun}>
      <IconButton
        aria-label={label}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? BIGrey : "text.secondary",
          "&:hover": {
            backgroundColor: disabled ? "transparent" : `${BIBlue}15`,
            color: disabled ? BIGrey : BIBlue
          },
          transition: "all 0.2s ease"
        }}>
        {icon}
      </IconButton>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          fontSize: "14px",
          color: disabled ? BIGrey : "text.secondary",
          minWidth: "20px",
          textAlign: "center"
        }}>
        {count >= 1000 ? `${(count / 1000).toFixed(1)}k` : count}
      </Typography>
    </Stack>
  );

  /************* Get Video Post *************/

  const getVideoPost = (postItem) => {
    const showMetrics = postItem?.icon === "" && postItem?.formatTypeId !== 0;
    return (
      <Card
        className="pagelist-content"
        sx={{
          bgcolor: postItem?.appId === 19 ? "rgb(255, 238, 191)" : "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)",
          borderRadius: "12px",
          overflow: "hidden",
          transition: "box-shadow 0.3s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
          }
        }}>
        {/* Header with Avatar, Name, Subtitle, Date, and Menu Icon */}
        <CardHeader
          sx={{ pb: 1 }}
          avatar={
            <Avatar
              alt="Profile Picture"
              src={postItem.appId === 19 ? mapMarkerYellow : postItem.postedByPictureUrl}
              sx={{
                width: postItem.appId === 19 ? 50 : 48,
                height: 48,
                border: `2px solid ${BIBlue}15`
              }}
            />
          }
          action={
            <>
              <IconButton
                aria-label="settings"
                onClick={handleMenuOpen}
                sx={{
                  "&:hover": {
                    backgroundColor: `${BIBlue}10`
                  }
                }}>
                <MoreVert />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                PaperProps={{
                  sx: {
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    mt: 1
                  }
                }}>
                {/* Show Mute/Unmute option if the post is by the current user */}
                {Number(postItem?.postedByUserId) ===
                  Number(loginData?.userInformation?.UserId) && (
                  <MenuItem
                    onClick={() => handleMutePost(postItem?.id, postItem?.isMute)}
                    sx={{
                      color: BIBlue,
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": { backgroundColor: `${BIBlue}10` }
                    }}>
                    {postItem?.isMute ? MORE_MENU_ON_NOTIFICATIONS : MORE_MENU_OFF_NOTIFICATIONS}
                  </MenuItem>
                )}

                <MenuItem
                  onClick={() => handleHidePost(postItem)}
                  sx={{
                    color: BIBlue,
                    fontSize: "14px",
                    py: 1.5,
                    px: 2,
                    "&:hover": { backgroundColor: `${BIBlue}10` }
                  }}>
                  {MORE_MENU_HIDE_POST}
                </MenuItem>

                {/* Report Post option */}
                <MenuItem
                  onClick={() => handleReportPost(postItem)}
                  sx={{
                    color: BIRed,
                    fontSize: "14px",
                    py: 1.5,
                    px: 2,
                    "&:hover": { backgroundColor: `${BIRed}10` }
                  }}>
                  {MORE_MENU_REPORT_POST}
                </MenuItem>
              </Menu>
            </>
          }
          title={
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "text.primary",
                lineHeight: 1.4
              }}>
              {postItem?.postedByFirstName + " " + postItem?.postedByLastName}
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "13px",
                mt: 0.5
              }}>
              {postItem?.appId === 27
                ? moment(postItem?.dateCreated).format("MMMM DD, YYYY")
                : `${postItem?.tradeName ? `(${postItem?.tradeName}) • ` : ""}${moment(
                    postItem?.dateCreated
                  ).format("MMMM DD, YYYY")}`}
            </Typography>
          }
        />
        <CardContent className="post-content-container" sx={{ pt: 0, px: 2, pb: 2 }}>
          {/* Post Message (if any) */}
          {postItem?.message &&
            postItem?.message !== "<<<picture>>>" &&
            postItem?.message !== "<<<video>>>" && (
              <Typography
                variant="body1"
                sx={{
                  margin: "0px 10px 16px 10px",
                  lineHeight: 1.7,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  fontSize: "15px",
                  color: "text.primary",
                  letterSpacing: "0.01em"
                }}>
                {renderMessageContent(postItem)}
              </Typography>
            )}
          {/* Video Content */}
          <Box sx={{ mb: 2, borderRadius: "12px", overflow: "hidden" }}>
            <VideoPlayerComponent postItem={postItem} />
          </Box>
          {/* Divider with spacing */}
          <Divider sx={{ my: 2 }} />
          {/* Stats Section */}
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2, md: 3 }}
            alignItems="center"
            justifyContent="space-around"
            flexWrap="wrap"
            sx={{ gap: { xs: 1, sm: 0 } }}>
            {showMetrics && (
              <>
                <StatsItem
                  icon={<BarChart sx={{ fontSize: 20 }} />}
                  label={"Impressions"}
                  count={postItem?.nrOfUsersImpressionsPerPost || 0}
                  callFun={() => handleShowAlert("postImpressionModal")}
                />
                <StatsItem
                  icon={<RemoveRedEye sx={{ fontSize: 20 }} />}
                  label={"Views"}
                  count={postItem?.nrOfUsersViewedPost || 0}
                  callFun={() => handleShowAlert("postViewsModal")}
                />
              </>
            )}

            <StatsItem
              icon={<Comment sx={{ fontSize: 20 }} />}
              label="comment"
              count={postItem?.nrComments || 0}
              callFun={() => handleCommentToggler(postItem)}
            />
            {postItem?.isMute && (
              <StatsItem
                icon={<NotificationsOff sx={{ fontSize: 20, color: BIGrey }} />}
                label="Notification Off"
                disabled
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  /************* Get Site Post *************/

  const getSitePost = (postItem) => {
    const handleFlagAsActive = async (siteId) => {
      try {
        let payload = {
          UserId: loginData?.userInformation?.UserId,
          ReturnRecordId: true,
          ReturnRecordError: true,
          SaveList: [
            {
              Id: siteId
            }
          ]
        };
        let getResponse = await Postrequestcall(SITE_ACTIVE, payload, loginData?.accessToken);
        if (getResponse.status === 201) {
          // Update the site active status in posts state
          const updatedPosts = posts.map((post) => {
            if (post?.site?.id === siteId) {
              return {
                ...post,
                site: { ...post.site, active: true }
              };
            }
            return post;
          });
          setPosts([...updatedPosts]);
        }
      } catch (error) {
        setAnchorEl(null);
        console.log("SITE_ACTIVE-> Error When Making Site Active: ", error);
      } finally {
      }
    };

    const handleSiteImageSelect = async (event) => {
      try {
        setIsImageUploading(true);
        const files = Array.from(event.target.files);
        if (!files.length) {
          setIsImageUploading(false);
          return;
        }
        const userId = Number(loginData?.userInformation?.UserId);
        const obj = {
          UserId: userId,
          ReturnRecordId: true,
          ReturnRecordError: false,
          SaveList: [
            {
              Id: 0,
              FormatTypeId: 1,
              PostedByUserId: userId,
              AppId: 2,
              AppRecordId: postItem?.site?.id,
              Message: "<<<picture>>>",
              ModifiedBy: userId
            }
          ]
        };
        const postRes = await Postrequestcall(SAVE_POSTS, obj, loginData?.accessToken);
        const postId = postRes?.data?.data?.[0]?.objData?.id;
        if (!postId) throw new Error("Post ID not returned from SAVE_POSTS");
        // Concurrent image uploads using Promise.all
        await Promise.all(
          files.map((file) => {
            const formData = new FormData();
            formData.append("Id", 0);
            formData.append("PostId", postId);
            formData.append("ModifiedBy", userId);
            formData.append("Pictures", file);
            return Fileuploadrequestcall(SAVE_POST_PICTURE, formData, loginData?.accessToken);
          })
        );
        // Refresh or update the post to show the new pictures
        const updatedPosts = posts.map((post) => {
          if (post?.site?.id === postItem?.site?.id) {
            return {
              ...post,
              site: {
                ...post.site,
                pictureUrl: URL.createObjectURL(files[files.length - 1]) // use the first uploaded file
              }
            };
          }
          return post;
        });
        setPosts([...updatedPosts]);
        setIsImageUploading(false);
      } catch (error) {
        console.error("handleSiteImageSelect-> Error When Saving Site Picture: ", error);
      } finally {
        setIsImageUploading(false);
      }
    };

    const handleAddPictures = () => {
      fileInputRef.current.click();
    };

    const handleFollowSite = async (e, siteId, isFollow) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        setFollowLoaderId(siteId);
        const obj = {
          UserId: Number(loginData?.userInformation?.UserId),
          ReturnRecordId: true,
          ReturnRecordError: true,
          SaveList: [
            {
              Id: 0,
              EntityTypeId: 25,
              EntityId: Number(siteId),
              UserId: Number(loginData?.userInformation?.UserId),
              IsFollowing: !isFollow,
              ModifiedBy: Number(loginData?.userInformation?.UserId)
            }
          ]
        };
        const res = await Postrequestcall(SAVE_FOLLOW_ENTITIES, obj, loginData?.accessToken);
        if (res?.data?.message === "OK") {
          // Update the site follow status in posts state
          const updatedPosts = posts.map((post) => {
            if (post?.site?.id === siteId) {
              return {
                ...post,
                site: { ...post.site, isFollowing: !isFollow }
              };
            }
            return post;
          });
          setPosts([...updatedPosts]);
        }
        setFollowLoaderId(null);
      } catch (error) {
        setFollowLoaderId(null);
        console.log("SAVE_FOLLOW_ENTITIES-> Error When Chaging Follow Status: ", error);
      }
    };

    const handleAddDescription = () => {
      dispatch(setSelectedsite(postItem?.site));
      setAddDescriptionModalOpen(true);
      setAnchorEl(null);
    };

    const handleAddWebsite = () => {
      setSiteInfo(postItem?.site);
      setAddSiteWebsiteModalOpen(true);
      setAnchorEl(null);
    };

    const navigateToSitedetail = (postItem) => {
      dispatch(setSelectedsite(postItem?.site));
      navigate(`/sites`, { state: { site: postItem?.site } });
    };

    return (
      <>
        <Card
          className="pagelist-content"
          sx={{
            bgcolor: "#ffffff",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)",
            borderRadius: "12px",
            overflow: "hidden",
            transition: "box-shadow 0.3s ease, transform 0.2s ease",
            "&:hover": {
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
            }
          }}>
          {/* Header with Avatar, Name, Trade, and Menu Icon */}
          <CardHeader
            sx={{ pb: 1 }}
            avatar={
              <Avatar
                alt="Profile Picture"
                src={postItem?.postedByPictureUrl || mapMarkerYellow}
                sx={{
                  width: 48,
                  height: 48,
                  border: `2px solid ${BIBlue}15`
                }}
              />
            }
            action={
              <>
                <IconButton
                  aria-label="settings"
                  onClick={handleMenuOpen}
                  sx={{
                    "&:hover": {
                      backgroundColor: `${BIBlue}10`
                    }
                  }}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  PaperProps={{
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      mt: 1
                    }
                  }}>
                  {postItem?.site?.active !== true && (
                    <MenuItem
                      onClick={() => handleFlagAsActive(postItem?.site?.id)}
                      sx={{
                        color: BIBlue,
                        fontSize: "14px",
                        py: 1.5,
                        px: 2,
                        "&:hover": { backgroundColor: `${BIBlue}10` }
                      }}>
                      {TEXT_FLAG_ACTIVE}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => handleAddPictures()}
                    sx={{
                      color: BIBlue,
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": { backgroundColor: `${BIBlue}10` }
                    }}>
                    {TEXT_ADD_PICTURE}
                  </MenuItem>
                  <MenuItem
                    onClick={handleAddDescription}
                    sx={{
                      color: BIBlue,
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": { backgroundColor: `${BIBlue}10` }
                    }}>
                    {TEXT_ADD_DESCRIPTION}
                  </MenuItem>
                  {(postItem?.site?.website === null || postItem?.site?.website === "") && (
                    <MenuItem
                      onClick={handleAddWebsite}
                      sx={{
                        color: BIBlue,
                        fontSize: "14px",
                        py: 1.5,
                        px: 2,
                        "&:hover": { backgroundColor: `${BIBlue}10` }
                      }}>
                      {BUTTON_ADD_WEBSITE}
                    </MenuItem>
                  )}
                  {/* Report Post option */}
                  <MenuItem
                    onClick={() => handleReportPost(postItem)}
                    sx={{
                      color: BIRed,
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": { backgroundColor: `${BIRed}10` }
                    }}>
                    {MORE_MENU_REPORT_POST}
                  </MenuItem>
                </Menu>
              </>
            }
            title={
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "text.primary",
                  lineHeight: 1.4,
                  cursor: "pointer"
                }}
                onClick={() => handleNavigation(postItem)}>
                {`${formattedName} ${postItem?.tradeName ? `(${postItem?.tradeName})` : ""}`}
              </Typography>
            }
            subheader={
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "13px",
                  mt: 0.5
                }}>
                Suggested
              </Typography>
            }
          />
          <CardContent className="post-content-container" sx={{ pt: 0, px: 2, pb: 2 }}>
            <Stack direction="column" spacing={2}>
              {/* Icon */}
              {postItem?.icon && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {getPostIcon(postItem?.icon)}
                </Box>
              )}

              {/* Site Description */}
              <Box sx={{ textAlign: "center", marginTop: "0px !important" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    mb: 0.5,
                    color: "text.primary"
                  }}>
                  Added a new site in your area
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "14px"
                  }}>
                  Know about this site? Add more information and earn BuilderBucks!
                </Typography>
              </Box>

              {/* Site Info Box */}
              <Box
                sx={{
                  border: `1px solid #e0e0e0`,
                  borderRadius: "12px",
                  margin: "0px 15px !important",
                  cursor: "pointer",
                  p: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  backgroundColor: "#fafafa",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)"
                  }
                }}
                onClick={() => navigateToSitedetail(postItem)}>
                <Box sx={{ flex: "1 1 auto" }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "15px",
                      wordBreak: "break-word",
                      mb: 0.5,
                      color: "text.primary"
                    }}>
                    {postItem?.site?.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "13px",
                      wordBreak: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}>
                    {[
                      postItem?.site?.province,
                      postItem?.site?.address,
                      postItem?.site?.city,
                      postItem?.site?.country
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", sm: "center" }
                  }}>
                  <Button
                    variant={postItem?.site?.isFollowing ? "outlined" : "contained"}
                    color="primary"
                    size="small"
                    onClick={(e) =>
                      handleFollowSite(e, postItem?.site?.id, postItem?.site?.isFollowing)
                    }
                    sx={{
                      minWidth: "90px",
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: "8px",
                      transition: "all 0.2s ease"
                    }}
                    disabled={followLoaderId === postItem?.site?.id}>
                    {followLoaderId === postItem?.site?.id ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : postItem?.site?.isFollowing ? (
                      TEXT_FOLLOWING
                    ) : (
                      TEXT_FOLLOW
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Site Image */}
              <Box
                sx={{
                  mx: 1,
                  borderRadius: "12px",
                  overflow: "hidden",
                  backgroundColor: isImageUploading ? "#fafafa" : "#f0f0f0",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)"
                }}>
                {isImageUploading ? (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    height={400}
                    sx={{
                      borderRadius: 0,
                      bgcolor: "rgba(0,0,0,0.08)",
                      "&::after": {
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)"
                      }
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "300px"
                    }}>
                    <img
                      src={postItem?.site?.pictureUrl || defaultProjectImage}
                      alt={postItem?.site?.name || "Site"}
                      style={{
                        width: "100%",
                        maxHeight: "400px",
                        objectFit: "contain",
                        display: "block",
                        backgroundColor: "#fff"
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Stack>

            {/* Divider with spacing */}
            <Divider sx={{ my: 2 }} />

            {/* Comment Stats - Centered */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <StatsItem
                icon={<Comment sx={{ fontSize: 20 }} />}
                label="comment"
                count={postItem?.nrComments || 0}
                callFun={() => handleCommentToggler(postItem)}
              />
            </Box>
          </CardContent>
        </Card>
        <input
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleSiteImageSelect}
        />
      </>
    );
  };

  /************* Get Normal Post (Post with Image, Montage Post) *************/

  const MontagePost = () => {
    const audioRef = useRef();
    /* State for audio/global play */
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);

    const [currentSlide, setCurrentSlide] = useState(0);
    const videoRefs = useRef({});
    const carouselRef = useRef();
    const musicUrl = postItem?.musicUrl;

    // Helper function to check if URL is a video file
    const isVideoUrl = (url) => {
      if (!url) return false;
      const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".m4v"];
      const lowerUrl = url.toLowerCase();
      return videoExtensions.some((ext) => lowerUrl.includes(ext));
    };

    const handleChange = (index) => {
      setCurrentSlide(index);
    };

    const togglePlay = () => {
      // Check if we have valid audio
      const hasValidAudio =
        musicUrl &&
        !musicUrl.endsWith("/") &&
        (musicUrl.includes(".mp3") ||
          musicUrl.includes(".wav") ||
          musicUrl.includes(".ogg") ||
          musicUrl.includes(".m4a"));

      if (hasValidAudio && audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current
            .play()
            .catch((error) => console.error("togglePlay-> Audio play error: ", error));
        } else {
          audioRef.current.pause();
        }
      }

      // Toggle playback state regardless of audio availability
      setIsAudioPlaying(!isAudioPlaying);

      // Record view when user plays montage
      if (!isAudioPlaying) {
        recordPostView("montage-play");
      }
    };

    // Handle audio playback
    useEffect(() => {
      if (!audioRef.current || !musicUrl) return;

      // Validate musicUrl is a complete URL with a file
      const isValidMusicUrl =
        !musicUrl.endsWith("/") &&
        (musicUrl.includes(".mp3") ||
          musicUrl.includes(".wav") ||
          musicUrl.includes(".ogg") ||
          musicUrl.includes(".m4a"));

      if (!isValidMusicUrl) {
        console.warn("Invalid or incomplete musicUrl - montage will play without audio:", musicUrl);
        return;
      }

      audioRef.current.src = musicUrl;
      if (isAudioPlaying) {
        audioRef.current
          .play()
          .catch((error) => console.error("useEffect-> Audio play error: ", error));
      }
    }, [musicUrl, isAudioPlaying]);

    // Advance to next slide
    const goToNextSlide = useCallback(() => {
      setCurrentSlide((prev) => {
        const next = prev + 1;
        const total = postItem?.pictureList?.length || 0;
        if (next >= total) {
          // Loop back to start
          return 0;
        }
        return next;
      });
    }, [postItem?.pictureList?.length]);

    // Handle Playback Logic (Video & Image)
    useEffect(() => {
      if (!postItem?.pictureList) return;
      const currentMedia = postItem.pictureList[currentSlide];
      const isVideo = isVideoUrl(currentMedia?.pictureUrl);
      // Manage Video Playback via Refs
      Object.keys(videoRefs.current).forEach((key) => {
        const vid = videoRefs.current[key];
        if (vid) {
          if (parseInt(key) === currentSlide && isAudioPlaying && isVideo) {
            vid.play().catch(() => {});
          } else {
            vid.pause();
            if (parseInt(key) !== currentSlide) {
              vid.currentTime = 0; // Reset others
            }
          }
        }
      });
      if (!isAudioPlaying) return;
      if (!isVideo) {
        // Image: Auto-advance after 5 seconds
        const timer = setTimeout(() => {
          goToNextSlide();
        }, 5000);
        return () => clearTimeout(timer);
      }
      // Video: Handled by onEnded on the element itself
    }, [currentSlide, isAudioPlaying, postItem, goToNextSlide]);

    // Handle when video ends
    const handleVideoEnded = useCallback(() => {
      goToNextSlide();
    }, [goToNextSlide]);

    // Sync audio element state with React state
    useEffect(() => {
      const audioElement = audioRef.current;
      const handlePause = () => setIsAudioPlaying(false);
      const handlePlay = () => setIsAudioPlaying(true);

      if (audioElement) {
        audioElement.addEventListener("pause", handlePause);
        audioElement.addEventListener("play", handlePlay);
      }

      return () => {
        if (audioElement) {
          audioElement.removeEventListener("pause", handlePause);
          audioElement.removeEventListener("play", handlePlay);
        }
      };
    }, []);

    return (
      <>
        <audio ref={audioRef} />
        {postItem?.pictureList?.length > 0 && (
          <Box position="relative">
            <Carousel
              ref={carouselRef}
              selectedItem={currentSlide}
              onChange={handleChange}
              autoPlay={false}
              infiniteLoop={false}
              showThumbs={false}
              showArrows={false}
              showStatus={false}
              showIndicators={false}
              stopOnHover={false}>
              {postItem?.pictureList.map((media, index) => {
                const isVideo = isVideoUrl(media.pictureUrl);

                return (
                  <Box
                    key={index}
                    position="relative"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={500}
                    overflow="hidden">
                    {isVideo ? (
                      <video
                        src={media.pictureUrl}
                        controls={false}
                        ref={(el) => (videoRefs.current[index] = el)}
                        onEnded={handleVideoEnded}
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <img
                        src={media.pictureUrl}
                        alt={"Slide"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    )}
                  </Box>
                );
              })}
            </Carousel>

            <IconButton
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: BIBlue
              }}
              onClick={togglePlay}>
              {isAudioPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
            </IconButton>
          </Box>
        )}
      </>
    );
  };

  const getNormalPost = (postItem) => {
    const hasIcon = postItem?.icon !== undefined && postItem?.icon !== "";
    const showMetrics = postItem?.icon === "" && postItem?.formatTypeId !== 0;

    const getPostIcon = (iconName) => {
      const iconSx = { color: BIBlue, fontSize: 50 };
      switch (iconName) {
        case "Briefcase":
          return <BusinessCenter sx={iconSx} />;
        case "Map Pin":
          return <Room sx={iconSx} />;
        case "Wrench":
          return <Build sx={iconSx} />;
        case "Pencil":
          return <Edit sx={iconSx} />;
        default:
          return null;
      }
    };

    const navigateSubheader = (postItem) => {
      if (postItem?.appName === "Trade") {
        navigate(`/${postItem?.postedByUserId}`);
      }
    };

    return (
      <Card
        className="pagelist-content"
        sx={{
          bgcolor: postItem?.appId === 19 ? "rgb(255, 238, 191)" : "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.06)",
          borderRadius: "12px",
          overflow: "hidden",
          transition: "box-shadow 0.3s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)"
          }
        }}>
        {/* Header with Avatar, Name, Subtitle, Date, and Menu Icon */}
        <CardHeader
          sx={{ pb: 1 }}
          avatar={
            <Avatar
              alt="Profile Picture"
              src={postItem.appId === 19 ? mapMarkerYellow : postItem.postedByPictureUrl}
              sx={{
                width: postItem.appId === 19 ? 50 : 48,
                height: 48,
                border: `2px solid ${BIBlue}15`
              }}
            />
          }
          action={
            <>
              <IconButton
                aria-label="settings"
                onClick={handleMenuOpen}
                sx={{
                  "&:hover": {
                    backgroundColor: `${BIBlue}10`
                  }
                }}>
                <MoreVert />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                PaperProps={{
                  sx: {
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    mt: 1
                  }
                }}>
                {/* Show Mute/Unmute option if the post is by the current user */}
                {Number(postItem?.postedByUserId) ===
                  Number(loginData?.userInformation?.UserId) && (
                  <MenuItem
                    onClick={() => handleMutePost(postItem?.id, postItem?.isMute)}
                    sx={{
                      color: BIBlue,
                      fontSize: "14px",
                      py: 1.5,
                      px: 2,
                      "&:hover": { backgroundColor: `${BIBlue}10` }
                    }}>
                    {postItem?.isMute ? MORE_MENU_ON_NOTIFICATIONS : MORE_MENU_OFF_NOTIFICATIONS}
                  </MenuItem>
                )}

                <MenuItem
                  onClick={() => handleHidePost(postItem)}
                  sx={{
                    color: BIBlue,
                    fontSize: "14px",
                    py: 1.5,
                    px: 2,
                    "&:hover": { backgroundColor: `${BIBlue}10` }
                  }}>
                  {MORE_MENU_HIDE_POST}
                </MenuItem>

                {/* Report Post option */}
                <MenuItem
                  onClick={() => handleReportPost(postItem)}
                  sx={{
                    color: BIRed,
                    fontSize: "14px",
                    py: 1.5,
                    px: 2,
                    "&:hover": { backgroundColor: `${BIRed}10` }
                  }}>
                  {MORE_MENU_REPORT_POST}
                </MenuItem>
              </Menu>
            </>
          }
          title={
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "text.primary",
                lineHeight: 1.4,
                cursor: "pointer"
              }}
              onClick={() => {
                handleNavigation(postItem);
              }}>
              {postItem?.appRecordId === 0 ||
              postItem?.appId === 27 ||
              from === "productDetail" ||
              from === "projectDetailPage" ||
              from === "tradeDetail" // appid 27 means it's montage post
                ? postItem?.postedByFirstName + " " + postItem?.postedByLastName
                : postItem?.appRecordName}
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "13px",
                cursor: postItem?.appName === "Trade" ? "pointer" : "default"
              }}
              onClick={() => navigateSubheader(postItem)}>
              {postItem?.appRecordId === 0 ||
              from === "productDetail" ||
              from === "projectDetailPage" ||
              from === "tradeDetail"
                ? `${postItem?.tradeName} • ` +
                  `${moment(from === "productDetail" || from === "tradeDetail" || from === "projectDetailPage" ? postItem?.postedDate : postItem?.dateCreated).format("MMMM DD, YYYY")}`
                : postItem?.appId === 27
                  ? moment(postItem?.dateCreated).format("MMMM DD, YYYY")
                  : `${postItem?.postedByFirstName + " " + postItem?.postedByLastName}${postItem?.tradeName ? ` (${postItem?.tradeName}) • ` : " • "}${moment(
                      postItem?.dateCreated
                    ).format("MMMM DD, YYYY")}`}
            </Typography>
          }
        />
        <CardContent className="post-content-container" sx={{ pt: 0, px: 2, pb: 2 }}>
          {/* Post Icon */}
          {hasIcon ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 60,
                mt: 1
              }}>
              {getPostIcon(postItem?.icon)}
            </Box>
          ) : (
            <Box sx={{ height: 10 }} />
          )}

          {/* Post Message */}
          {postItem?.message && postItem?.message !== "<<<picture>>>" && (
            <Typography
              variant="body1"
              sx={{
                margin: "0px 10px 10px 10px",
                lineHeight: 1.6,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                fontSize: "15px",
                color: "text.primary",
                textAlign: hasIcon ? "center" : "left"
              }}>
              {renderMessageContent(postItem)}
            </Typography>
          )}

          {/* Media Content */}
          {postItem?.appId === 27 ? (
            <MontagePost />
          ) : (
            <Box sx={{ mb: 2 }}>
              {postItem?.pictureList?.length > 0 ? (
                <Carousel
                  showThumbs={false}
                  showIndicators={postItem.pictureList.length > 1}
                  showStatus={false}
                  renderIndicator={(onClickHandler, isSelected, index, label) => {
                    const defStyle = {
                      marginLeft: 4,
                      marginRight: 4,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      display: "inline-block",
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    };
                    const style = isSelected
                      ? {
                          ...defStyle,
                          backgroundColor: BIBlue,
                          width: 24,
                          borderRadius: "4px"
                        }
                      : { ...defStyle, backgroundColor: "#ccc" };
                    return (
                      <span
                        style={style}
                        onClick={onClickHandler}
                        onKeyDown={onClickHandler}
                        key={index}
                        role="button"
                        tabIndex={0}
                        aria-label={`${label} ${index + 1}`}
                      />
                    );
                  }}>
                  {postItem.pictureList.map((picture, index) => (
                    <Box
                      key={picture?.id || index}
                      className="add-post-picture-list"
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: "#f9f9f9",
                        position: "relative",
                        "&:hover": {
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.05)",
                            pointerEvents: "none"
                          }
                        }
                      }}
                      onClick={() => {
                        setPictureViewerOpen(true);
                        setPictureViewIndex(index);
                        recordPostView("image");
                      }}>
                      <img
                        src={picture?.pictureUrl || defaultProjectImage}
                        onError={(e) => {
                          e.target.src = defaultProjectImage;
                        }}
                        alt="Project"
                        loading="lazy"
                        style={{
                          width: "100%",
                          maxHeight: "500px",
                          objectFit: "contain",
                          display: "block"
                        }}
                      />
                    </Box>
                  ))}
                </Carousel>
              ) : (
                postItem?.message?.includes("<<<picture>>>") && <NoImageFound />
              )}
            </Box>
          )}

          {/* Divider with spacing */}
          <Divider sx={{ my: 2 }} />

          {/* Stats Section */}
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2, md: 3 }}
            alignItems="center"
            justifyContent="space-around"
            flexWrap="wrap"
            sx={{ gap: { xs: 1, sm: 0 } }}>
            {showMetrics && (
              <>
                <StatsItem
                  icon={<BarChart sx={{ fontSize: 20 }} />}
                  label={"Impressions"}
                  count={postItem?.nrOfUsersImpressionsPerPost || 0}
                  callFun={() => handleShowAlert("postImpressionModal")}
                />
                <StatsItem
                  icon={<RemoveRedEye sx={{ fontSize: 20 }} />}
                  label={"Views"}
                  count={postItem?.nrOfUsersViewedPost || 0}
                  callFun={() => handleShowAlert("postViewsModal")}
                />
              </>
            )}
            <StatsItem
              icon={<Comment sx={{ fontSize: 20 }} />}
              label="comment"
              count={postItem?.nrComments || 0}
              callFun={() => handleCommentToggler(postItem)}
            />
            {postItem?.isMute && (
              <StatsItem
                icon={<NotificationsOff sx={{ fontSize: 20, color: BIGrey }} />}
                label="Notification Off"
                disabled
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderPosts = (postItem) => {
    switch (postItem.formatTypeId) {
      case 5:
        return getSitePost(postItem);
      case 7:
        return getVideoPost(postItem);
      case 8:
        return <UserSuggestion data={postItem} />;
      default:
        return getNormalPost(postItem);
    }
  };

  return (
    <>
      {renderPosts(postItem)}

      {pictureViewerOpen && (
        <PictureViewer
          isOpen={pictureViewerOpen}
          onClose={() => setPictureViewerOpen(false)}
          {...(postItem?.pictureList?.length > 1
            ? {
                pictures: postItem.pictureList.map((pic) => ({
                  src: pic.pictureUrl,
                  alt: ""
                })),
                viewerIndex: pictureViewIndex
              }
            : { src: postItem.pictureList[0]?.pictureUrl })}
        />
      )}
      {openComment && (
        <PostComment
          open={openComment}
          postItem={selectPost}
          setSelectPost={setSelectPost}
          posts={posts}
          onClose={() => setOpenComment(false)}
          setPosts={setPosts}
        />
      )}
      {addDescriptionModalOpen && (
        <AddDescription
          getSiteDescriptions={() => {}}
          handleClose={() => {
            setAddDescriptionModalOpen(false);
          }}
          addDescriptionDialogOpen={addDescriptionModalOpen}
        />
      )}
      {addSiteWebsiteModalOpen && (
        <AddWebsiteModal
          open={addSiteWebsiteModalOpen}
          onClose={() => {
            setAddSiteWebsiteModalOpen(false);
          }}
          from="SiteCard"
          data={siteInfo || {}}
          onDataSave={() => onSiteWebsiteSave("website")}
          onSaveClick={() => {}}
        />
      )}
    </>
  );
}

export default Post;
